import { cloneDeep } from "lodash";
import { Contact } from "../interfaces/DTO/crm/contact";
import { MimshakData } from "../interfaces/DTO/mimshakData";
import {
  BeneficiaryIDCode,
  BeneficiaryIdentificationType,
  BeneficiaryInfo,
  BeneficiaryType,
} from "../interfaces/DTO/mislaka_xml_insurence_models";
import { RelationType } from "../interfaces/DTO/mislaka_xml_pension_models";

/**
 * The client's beneficiaries definition (Contact.data.beneficiaries) - in the Mislaka's own shape (BeneficiaryInfo),
 * so a fund's beneficiaries can be compared with it and set from it. Only the death beneficiaries are defined /
 * compared / replaced - a life beneficiary of a fund is kept as is.
 */

/** the relations a beneficiary is usually defined with */
export const BENEFICIARY_RELATION_OPTIONS: RelationType[] = [
  RelationType.SPOUSE,
  RelationType.CHILD,
  RelationType.PARENTS,
  RelationType.SIBLING,
  RelationType.OTHER,
];

export const newBeneficiary = (relationType: RelationType = RelationType.OTHER): BeneficiaryInfo => ({
  identification_type: BeneficiaryIdentificationType.INDIVIDUAL,
  id_code: BeneficiaryIDCode.ID_NUMBER,
  relation_type: relationType,
  beneficiary_nature: BeneficiaryType.DEATH_BENEFICIARY,
  beneficiary_percentage: 0,
  first_name: "",
  last_name: "",
  id_number: "",
});

/** percentages that add up to exactly 100 - an equal split, the remainder to the first */
const equalSplit = (count: number): number[] => {
  if (count === 0) return [];
  const share = Math.floor(100 / count);
  return Array.from({ length: count }, (_, i) => share + (i === 0 ? 100 - share * count : 0));
};

/**
 * the couple's children - a shared child may be on one spouse's family_data only (as the client card's children row shows them);
 * each child once (by id number, otherwise by name + birth date)
 */
export const familyChildren = (contact: Contact | undefined, spouse: Contact | undefined): BeneficiaryInfo[] => {
  const seen = new Set<string>();
  return [...(contact?.data?.family_data ?? []), ...(spouse?.data?.family_data ?? [])].filter((child) => {
    const key = child.id_number?.trim() || `${child.first_name ?? ""}|${child.last_name ?? ""}|${child.birth_date ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** the spouse and the children (familyChildren) - in equal parts */
export const suggestBeneficiariesFromFamily = (spouse: Contact | undefined, children: BeneficiaryInfo[]): BeneficiaryInfo[] => {
  const people: BeneficiaryInfo[] = [
    ...(spouse
      ? [{
          ...newBeneficiary(RelationType.SPOUSE),
          first_name: spouse.first_name ?? "",
          last_name: spouse.last_name ?? "",
          id_number: spouse.israeli_id ?? "",
          birth_date: spouse.date_of_birth,
        }]
      : []),
    ...children.map((child) => ({
      ...newBeneficiary(RelationType.CHILD),
      first_name: child.first_name ?? "",
      last_name: child.last_name ?? "",
      id_number: child.id_number ?? "",
      birth_date: child.birth_date,
    })),
  ];
  const shares = equalSplit(people.length);
  return people.map((b, i) => ({ ...b, beneficiary_percentage: shares[i] }));
};

export const totalPercentage = (beneficiaries: BeneficiaryInfo[]) =>
  beneficiaries.reduce((sum, b) => sum + (b.beneficiary_percentage || 0), 0);

/** a definition that can be applied - adds up to 100%, everyone identified */
export const definitionProblem = (beneficiaries: BeneficiaryInfo[]): string | null => {
  if (beneficiaries.length === 0) return "לא הוגדרו מוטבים";
  if (Math.abs(totalPercentage(beneficiaries) - 100) > 0.01) return `סך האחוזים ${totalPercentage(beneficiaries)}% – צריך להיות 100%`;
  if (beneficiaries.some((b) => !b.id_number?.trim())) return "חסר מספר זהות למוטב";
  if (beneficiaries.some((b) => !(b.beneficiary_percentage && b.beneficiary_percentage > 0))) return "לכל מוטב צריך אחוז גדול מ-0";
  return null;
};

const isDeathBeneficiary = (b: BeneficiaryInfo) => b.beneficiary_nature !== BeneficiaryType.LIFE_BENEFICIARY;

/** the death beneficiaries of a fund - id number -> percentage (legal heirs - "LEGAL_HEIRS") */
const deathBeneficiariesKey = (beneficiaries: BeneficiaryInfo[]): Map<string, number> => {
  const key = new Map<string, number>();
  beneficiaries.filter(isDeathBeneficiary).forEach((b) => {
    const id = b.identification_type === BeneficiaryIdentificationType.LEGAL_HEIRS ? "LEGAL_HEIRS" : (b.id_number ?? "").trim();
    key.set(id, (key.get(id) ?? 0) + (b.beneficiary_percentage ?? 0));
  });
  return key;
};

/** the fund's death beneficiaries are the definition's - the same people with the same percentages */
export const matchesDefinition = (fundBeneficiaries: BeneficiaryInfo[], definition: BeneficiaryInfo[]): boolean => {
  const fund = deathBeneficiariesKey(fundBeneficiaries);
  const wanted = deathBeneficiariesKey(definition);
  if (fund.size !== wanted.size) return false;
  return Array.from(wanted.entries()).every(([id, pct]) => fund.has(id) && Math.abs((fund.get(id) ?? 0) - pct) < 0.5);
};

/**
 * the fund (a MimshakData row) with the definition as its death beneficiaries - in every coverage of the policy;
 * its life beneficiaries are kept
 */
export const withBeneficiaries = (row: MimshakData, policyNumber: string, definition: BeneficiaryInfo[]): MimshakData => {
  const updated = cloneDeep(row);
  updated.data?.products?.forEach((product) =>
    product.policies_or_accounts?.forEach((policy) => {
      if (policy.policy_number !== policyNumber) return;
      policy.insurence_coverages?.forEach((coverage) => {
        const lifeBeneficiaries = (coverage.beneficiaries ?? []).filter((b) => !isDeathBeneficiary(b));
        coverage.beneficiaries = [...lifeBeneficiaries, ...definition.map((b) => ({ ...b, beneficiary_nature: BeneficiaryType.DEATH_BENEFICIARY }))];
      });
    })
  );
  return updated;
};

export const beneficiaryName = (b: BeneficiaryInfo) => `${b.first_name ?? ""} ${b.last_name ?? ""}`.trim() || b.id_number || "מוטב";
